<template>
  <div class="box p-0 order-group" :class="{ 'is-created': readonly, 'is-dirty': isDirty }">
    <div class="px-4 py-3 is-flex is-align-items-center gap-3 order-group-header">
      <div class="is-flex is-align-items-center gap-2 is-flex-grow-1">
        <span class="is-size-7 has-text-weight-bold has-text-grey is-uppercase order-for-label">
          Order for
        </span>
        <span class="has-text-weight-bold is-size-5" data-supplier>{{ supplierLabel }}</span>
        <span class="tag">{{ items.length }} item{{ items.length !== 1 ? "s" : "" }}</span>
        <PurchaseOrderState
          v-if="persistedOrder?.state"
          :state="persistedOrder.state"
          data-po-state
        />
        <a
          v-if="persistedOrder && readonly"
          :href="persistedOrder.path"
          class="tag is-success is-medium po-created-badge"
          target="_blank"
        >
          &#x2713; {{ persistedOrder.visual_id }}
          <span class="ml-1 is-size-7">View order &rarr;</span>
        </a>
        <a
          v-else-if="persistedOrder"
          :href="persistedOrder.path"
          class="tag is-info is-light is-medium po-created-badge"
          target="_blank"
        >
          {{ persistedOrder.visual_id }}
          <span class="ml-1 is-size-7">View order &rarr;</span>
        </a>
        <span v-if="isDirty" class="tag is-warning is-medium">Unsaved changes</span>
      </div>
      <button
        v-if="persistedOrder && !readonly"
        class="button is-primary is-small"
        :class="{ 'is-loading': isUpdating }"
        :disabled="!isDirty || isUpdating"
        data-update-order
        @click="$emit('update')"
      >
        Update order
      </button>
      <button
        v-else-if="!persistedOrder && !readonly"
        class="button is-info is-small"
        :class="{ 'is-loading': isCreating }"
        :disabled="hasInvalidItems || isCreating"
        :title="hasInvalidItems ? 'All items need a positive quantity' : undefined"
        @click="showConfirm = true"
      >
        Create this order &rarr;
      </button>
    </div>

    <table class="table is-fullwidth mb-0 order-items-table">
      <thead>
        <tr>
          <th>Ingredient</th>
          <th>Quantity</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="items.length === 0">
          <td colspan="3" class="has-text-grey-light has-text-centered py-4" data-empty-order>
            No items yet — add ingredients above to populate this order.
          </td>
        </tr>
        <tr v-for="item in items" :key="item.key" class="order-item-row">
          <td class="has-text-weight-bold">
            {{ item.name }}
            <span
              v-if="!item.order_item_id && isNewSupplier(item)"
              class="tag is-warning is-light is-small ml-1"
              data-new-supplier
            >
              New supplier
            </span>
          </td>
          <td>
            <div class="field has-addons mb-0">
              <div class="control">
                <input
                  v-field-errors
                  type="number"
                  class="input is-small po-qty-input"
                  min="0"
                  step="any"
                  :value="item.qty"
                  :disabled="readonly || isCreating || isUpdating"
                  :data-errors="itemErrors(item)"
                  @input="
                    $emit('update-item', { key: item.key, patch: { qty: $event.target.value } })
                  "
                />
              </div>
              <div class="control">
                <div class="select is-small">
                  <select
                    class="po-unit-select"
                    :value="item.unit"
                    :disabled="readonly || isCreating || isUpdating"
                    @change="
                      $emit('update-item', { key: item.key, patch: { unit: $event.target.value } })
                    "
                  >
                    <option v-for="opt in item.unit_options" :key="opt.unit" :value="opt.unit">
                      {{ opt.unit }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <p
              v-if="feedbackType(item)"
              class="is-size-7 mt-1 po-feedback"
              :class="
                feedbackType(item) === 'below' ? 'has-text-warning-dark' : 'has-text-grey-light'
              "
            >
              Needs: {{ fmtQty(suggestedQty(item)) }}&thinsp;{{ item.unit }}
            </p>
          </td>
          <td class="has-text-right">
            <button
              v-if="!readonly"
              class="button is-outlined is-small is-danger"
              data-remove-item
              @click="itemToRemove = item"
            >
              Remove
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <ConfirmDialog
      v-if="itemToRemove"
      :model-value="true"
      title="Remove item"
      confirm-text="Remove"
      @confirm="confirmRemoveItem"
      @update:model-value="itemToRemove = null"
    >
      Remove <strong>{{ itemToRemove.name }}</strong> from this order?
    </ConfirmDialog>

    <ConfirmDialog
      v-model="showConfirm"
      title="Create order"
      confirm-text="Create order"
      confirm-class="is-info"
      @confirm="$emit('create')"
    >
      Create a purchase order for {{ supplierLabel }}?
      <template v-if="itemsBelowNeeded.length > 0">
        <br />
        <span class="is-size-7 has-text-warning-dark">
          Note: {{ itemsBelowNeeded.length }} item{{
            itemsBelowNeeded.length !== 1 ? "s have" : " has"
          }}
          quantities below the suggested purchase amount.
        </span>
      </template>
    </ConfirmDialog>
  </div>
</template>

<script setup>
  import { computed, ref } from "vue";
  import { toUnit, fmtQty, qtyFeedbackType } from "./unit-utils.js";
  import ConfirmDialog from "../shared/confirm-dialog.vue";
  import PurchaseOrderState from "../purchase-order-state.vue";
  import { vFieldErrors } from "~/jose_modern/field_with_errors";

  const props = defineProps({
    supplierLabel: { type: String, required: true },
    items: { type: Array, required: true },
    persistedOrder: { type: Object, default: null },
    readonly: { type: Boolean, default: false },
    isCreating: { type: Boolean, default: false },
    isDirty: { type: Boolean, default: false },
    isUpdating: { type: Boolean, default: false },
  });

  const emit = defineEmits(["update-item", "remove-item", "create", "update"]);

  const showConfirm = ref(false);
  const itemToRemove = ref(null);

  function confirmRemoveItem() {
    const { key, order_item_id: orderItemId } = itemToRemove.value;
    emit("remove-item", { key, orderItemId: orderItemId ?? null });
    itemToRemove.value = null;
  }

  const hasInvalidItems = computed(() => props.items.some((i) => !(parseFloat(i.qty) > 0)));

  function isNewSupplier(item) {
    if (!item.supplier_id || !item.ingredient_supplier_ids) return false;
    return !item.ingredient_supplier_ids.includes(item.supplier_id);
  }

  function suggestedQty(item) {
    return toUnit(Math.max(item.to_purchase ?? 0, 0), item.base_unit, item.unit, item.unit_options);
  }

  function feedbackType(item) {
    return qtyFeedbackType(item.qty, suggestedQty(item));
  }

  function itemErrors(item) {
    if (parseFloat(item.qty) > 0) return undefined;
    return JSON.stringify({ Quantity: ["must be greater than 0"] });
  }

  const itemsBelowNeeded = computed(() => props.items.filter((i) => feedbackType(i) === "below"));
</script>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  .order-group {
    border: 1px solid $border;
    border-left-width: 4px;
    overflow: hidden;
    transition:
      border-color 200ms,
      border-left-color 200ms;

    &.is-created {
      border-color: #ade5c0;

      .order-group-header {
        background: #f4fbf6;
      }
    }

    &.is-dirty {
      border-left-color: $warning;
    }

    .order-group-header {
      border-bottom: 1px solid $border;
    }

    .order-for-label {
      letter-spacing: 0.05em;
    }

    .po-created-badge {
      text-decoration: none;
      height: auto;
      padding: 4px 10px;
    }

    .order-items-table th,
    .order-items-table td {
      vertical-align: top;
      padding-top: 0.6rem;
    }

    .po-qty-input {
      width: 84px;
      text-align: right;
      appearance: textfield;
    }

    .po-unit-select {
      width: 72px;
    }
  }
</style>
